import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import { BodyNormal, Subtitle } from "@ui/fonts";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  useApplyForEducationalEventMutation,
  useDeleteEventMutation,
  useStudyCenterEducationalEventQuery,
} from "@lib/study-center";

import { BackButton } from "@components/back-button";
import { Button } from "@ui/button";
import React from "react";
import { StudyCenterEvent } from "@interfaces/study-center";
import clsx from "clsx";
import { getTextWithAnchors } from "@utils/common-utils";
import moment from "moment";
import styles from "./educational-event-page.module.scss";
import { useModal } from "@modals/index";
import { useUnit } from "effector-react";

type EducationalEventsPageParams = {
  id?: string;
};

type EducationalEventPageProps = {
  setTitle?: (value: string) => void;
};

export const EducationalEventPage: React.FC<EducationalEventPageProps> = ({ setTitle }) => {
  const history = useNavigate();
  const articleId = useParams<EducationalEventsPageParams>()?.id;

  const modal = useModal();

  const id = typeof articleId === "undefined" ? undefined : +articleId;
  const { event } = useStudyCenterEducationalEventQuery(id);

  const applyForEventMutation = useApplyForEducationalEventMutation();
  const deleteEventMutation = useDeleteEventMutation();

  const formattedDate = moment(event?.date).format("DD MMM YYYYг., HH:mm");

  React.useEffect(() => {
    if (event && setTitle) setTitle(event.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const handleDelete = () => {
    if (!event) return;

    modal.open("ConfirmActionModal", {
      btnText: "Удалить",
      modalData: {
        text: `Вы уверены, что хотите удалить событие ${event.title}?`,
        style: {
          maxWidth: "500px",
        },
      },
      onConfirm: () => {
        deleteEventMutation.mutate(event.id, {
          onSuccess: () => {
            history("/study-center/materials/educational-events");
          },
        });
      },
    });
  };

  const handleApply = () => {
    modal.open("SelectCompanyModal", {
      modalData: {
        title: "Для записи на событие выберите компанию.",
      },
      onConfirm: (companyId: number | undefined) => {
        if (companyId === undefined || id === undefined) return;

        applyForEventMutation.mutate(
          {
            id,
            companyId,
          },
          {
            onSuccess: () => {
              modal.open("ConfirmActionModal", {
                btnText: "Хорошо",
                modalData: {
                  title: "Заявка отправлена",
                  text: "Спасибо! Ваша заявка отправлена. В ближайшее время с вами свяжется Администратор для уточнения деталей",
                  removeIcon: true,
                  removeCancelBtn: true,
                  style: {
                    maxWidth: "500px",
                  },
                },
              });
            },
          }
        );
      },
    });
  };

  if (!event) {
    return null;
  }

  return (
    <div className={styles.wrapper_article}>
      <div className={styles.header_block}>
        <BackButton />
        <Button
          color="green"
          onClick={handleApply}
          className={styles.Article__applyBtn}
        >
          Участвовать
        </Button>
      </div>
      <div className={styles.Article}>
        <div className={styles.Article__leftSide}>
          <div className={styles.Article__backBtn} tabIndex={0}></div>
          <AdminControls
            className={styles.Article__mobileAdminControls}
            material={event}
            onDelete={handleDelete}
          />
        </div>
        <div className={styles.Article__content}>
          <div className={styles.Article__controls}>
            <Subtitle weight="bold">{event.title}</Subtitle>
          </div>
          <div className={styles.Article__body}>
            {event.imagePath && (
              <img
                src={`${process.env.API_URL}/uploads/educational-center/offers/events/${event.imagePath}`}
                alt={event.title}
                className={styles.Article__image}
              />
            )}
            <BodyNormal weight="bold">{formattedDate}</BodyNormal>
            <div
              dangerouslySetInnerHTML={{
                __html: getTextWithAnchors(event.description),
              }}
            />
          </div>
        </div>
        <AdminControls
          className={styles.Article__adminControls}
          material={event}
          onDelete={handleDelete}
        />
      </div>
    </div>
  );
};

type AdminControlsProps = {
  material: StudyCenterEvent;
  className: string;
  onDelete: () => void;
};

const AdminControls: React.FC<AdminControlsProps> = ({
  material,
  className,
  onDelete,
}) => {
  const permissions = useUnit($UserAddPermissions);

  return (
    <div className={className}>
      {permissions.hasPermission(
        ACTIONS.educationalCenter_materials_allowedToEdit
      ) && (
        <Link
          to={`/study-center/materials/educational-events/edit/${material.id}`}
        >
          <button
            className={clsx(
              styles.Article__adminControlsBtn,
              "colorful_action_btn"
            )}
          >
            Изменить
          </button>
        </Link>
      )}
      {permissions.hasPermission(
        ACTIONS.educationalCenter_materials_allowedToDelete
      ) && (
        <button
          className={clsx(
            styles.Article__adminControlsBtn,
            "colorful_action_btn"
          )}
          onClick={onDelete}
        >
          Удалить
        </button>
      )}
    </div>
  );
};
